import * as React from 'react';
import {
  type ExpressionPath,
  Expressions,
  Plugins,
  withSystem,
  matchExpression,
  Component,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  Button,
  CheckDefaultIcon,
  ChevronSmallDownIcon,
  ChevronSmallLeftIcon,
  ChevronSmallRightIcon,
  CloseButton,
  Divider,
  ElementTabsIcon,
  FormGroup,
  HStack,
  IconButton,
  Menu,
  MenuItem,
  MenuContent,
  MenuTrigger,
  Text,
  TextInput,
  Tooltip,
  View,
  VStack,
} from '@packages/systems/spring';
import {
  ExpressionEditor,
  updateExpressionAction,
  extendExpressionEditorActionAtPath,
  useExpressionEditorContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TRecord,
  TTypeConstructor,
  EList,
  EText,
  type Expression,
  TText,
} from '@packages/systems/wfdl-ast';
import {emotionStyled} from '@packages/utilities/styled';
import {typeName, qualify, namesEqual} from '@packages/systems/wfdl-Qualified';
import {
  concatTo,
  constant,
  constantFalse,
  emptyArray,
} from '@packages/utilities/fp/utils';
import PropertyList, {
  PropertyListItem,
  PropertyListItemID,
  PropertyListNone,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {
  DeprecatedPopoverBridge,
  CLOSED_STATE,
  OPENED_STATE,
} from '@packages/systems/design-system/PopoverBridge';
import {Subheading} from '@packages/systems/design-system/Text';
import * as KeyUtils from '@packages/utilities/key-utils';
import {
  CHILDREN_PATH,
  FADE_IN_PATH,
  FADE_OUT_PATH,
  EASING_PATH,
  DEFAULT_TAB_PATH,
  DATA_W_TAB_PATH,
  ELEMENT_CHILDREN_PATH,
  NONE_TAB_ID,
} from '../constants';
import {TEMP_TABS_CURRENT_PATH} from '@packages/utilities/expression-path-constants';
import {TabsContext} from '../TabsContext';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {selectIsOnPrimaryLocale} from '@packages/systems/localization';
import {useFluxContext} from '@packages/systems/designer/flux';

const Separator = emotionStyled('div')({
  width: '100%',
  height: '$2',
});

const HeaderContainer = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '$2',
  borderBottom: '$border1',
});

const FlexColumn = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
});

const Title = emotionStyled('header')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const DefaultLabel = emotionStyled('span')({
  color: '$textSecondary',
  display: 'inline-block',
});

const StyledLabelContainer = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '$1',
  height: '$5',
});

// @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type. | TS7031 - Binding element 'onClose' implicitly has an 'any' type.
const TooltipContent = ({children, onClose, showHeader = true, title = ''}) => {
  return (
    <FlexColumn>
      {showHeader && (
        <HeaderContainer>
          <Title>
            {/* @ts-expect-error - TS2769 - No overload matches this call. */}
            <Subheading small>{title}</Subheading>
          </Title>
          <CloseButton onClick={onClose} />
        </HeaderContainer>
      )}
      {children}
    </FlexColumn>
  );
};

const TabsList = ({
  items,
  currentTabId,
  defaultTabId,
  onItemDeleted,
  onItemDropped,
  onSelectClick,
  onEditClick,
}: {
  items: Tab[];
  currentTabId: string;
  defaultTabId: string;
  onItemDeleted: (id: PropertyListItemID) => void;
  onItemDropped: (arg: {
    itemId: PropertyListItemID;
    dropTargetId: PropertyListItemID;
  }) => void;
  onSelectClick: (id: string) => void;
  onEditClick: (oldId: string, newId: string) => void;
}) => {
  const [editId, setEditId] = React.useState(-1);
  const [listItemValue, setListItemValue] = React.useState('');
  const [isNameTaken, setIsNameTaken] = React.useState(false);
  const [hoveredItem, setHoveredItem] = React.useState(-1);

  // `items` is intentionally omitted since we only want to pre-fill the rename
  // tab name input when the user starts editing a different tab, not when the items
  // array changes (which would overwrite what the user is typing).
  React.useEffect(() => {
    const isEditing = editId !== -1;
    const tabName = isEditing ? items[editId]?.id : undefined;
    setListItemValue(tabName ?? NONE_TAB_ID);
  }, [editId]);

  const flux = useFluxContext();
  const isMiniSettingsOpen =
    'TabsWrapper' in flux.state.UiStore.miniSettingsFlags;
  // Tree structure changes are not allowed in secondary locales
  const isOnPrimaryLocale = selectIsOnPrimaryLocale(
    flux.state.LocalizationStore
  );

  const showMask = () => {
    return isMiniSettingsOpen ? 'TRANSPARENT' : true;
  };

  const renderNone = () => <PropertyListNone />;

  const renderListItem = (listItem: Tab, idx: number) => {
    const close = () => {
      setEditId(-1);
    };

    const save = () => {
      onEditClick(listItem.id, String(listItemValue));
      close();
    };

    const isTabNameTaken = (tabName: string) => {
      return items.some((item) => item.id === tabName);
    };

    const isValidName = (tabName: string) => {
      return tabName !== listItem.id && isTabNameTaken(tabName);
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentTabName = e.target.value;
      setListItemValue(currentTabName);
      setIsNameTaken(isValidName(currentTabName));
    };

    const isNoneTab = () => {
      return listItem.id === NONE_TAB_ID;
    };

    const isDefault = listItem.id === defaultTabId;
    const name = isNoneTab() ? 'None' : listItem.id;
    const label = isDefault ? (
      <>
        {name}{' '}
        <Tooltip label="This tab is shown by default when the site loads">
          <DefaultLabel>Default</DefaultLabel>
        </Tooltip>
      </>
    ) : (
      name
    );
    const isNotNoneTab = !isNoneTab();

    const renderPopoverContent = () => {
      return (
        <TooltipContent onClose={close} title="Rename Tab">
          <View padding="$2" width={224}>
            <VStack>
              <FormGroup label="Name" isHorizontal labelWidth={45}>
                <TextInput
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  isError={isNameTaken}
                  onChange={onChange}
                  value={String(listItemValue)}
                  onKeyPress={(e) => {
                    if (e.which === KeyUtils.ENTER && !isNameTaken) save();
                  }}
                />
              </FormGroup>
              {isNameTaken ? (
                <View marginLeft={45}>
                  <Text colorVariant="danger">This name is already taken.</Text>
                </View>
              ) : null}
              <View display="flex" justifyContent="flex-end">
                <Button isDisabled={isNameTaken} onClick={save}>
                  Save
                </Button>
              </View>
            </VStack>
          </View>
        </TooltipContent>
      );
    };

    return (
      <DeprecatedPopoverBridge
        // @ts-expect-error - TS2769 - No overload matches this call.
        key={idx}
        onChange={(popoverState) => {
          if (popoverState.open === false) close();
        }}
        state={idx === editId ? OPENED_STATE : CLOSED_STATE}
        renderPopoverContent={renderPopoverContent}
        showArrow={false}
        showMask={showMask()}
        metricsIdentifier="tabs-wrapper-type-popover"
      >
        {({Anchor}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <PropertyListItem
                _ref={ref}
                checkedIndicator="radio"
                checkedIndicatorTooltip={
                  isNotNoneTab ? 'View this tab on the canvas' : undefined
                }
                isSelected={currentTabId === listItem.id}
                showIconsByDefault
                deletable={isNotNoneTab && isOnPrimaryLocale}
                sortable={isNotNoneTab && isOnPrimaryLocale}
                editable={isNotNoneTab}
                key={idx}
                id={String(idx)}
                label={label}
                // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
                hover={hoveredItem === listItem.id}
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<number>'.
                onMouseOver={() => setHoveredItem(listItem.id)}
                onMouseOut={() => setHoveredItem(-1)}
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<number>'.
                onFocus={() => setHoveredItem(listItem.id)}
                onBlur={() => setHoveredItem(-1)}
                onClick={() => {
                  onSelectClick(listItem.id);
                }}
              />
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    );
  };

  return (
    <View marginBottom="$2">
      <PropertyList
        sortable
        deletable
        disabled={false}
        onItemDeleted={onItemDeleted}
        onItemDropped={onItemDropped}
        onItemEdited={(id) => {
          setEditId(parseInt(id, 10));
        }}
        items={items}
        renderNone={renderNone}
        renderItem={renderListItem}
      />
    </View>
  );
};

const fields = {
  children: withSystem(
    {label: 'Add tab', labelVariant: 'hidden'},
    TTypeConstructor(qualify(Plugins.name('Tabs'), typeName('AddTab')))
  ),
  tabs: TRecord({
    type: withSystem({defaultValue: 'wrapper', private: true}, TText),
    current: withSystem(
      {label: 'Default', defaultValue: '', isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Tabs'), typeName('DefaultTab')))
    ),
    easing: withSystem(
      {label: 'Easing', defaultValue: 'ease', isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('EasingsEnum')))
    ),
    fadeIn: withSystem(
      {label: 'Fade in', defaultValue: 300, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('MSNumber')))
    ),
    fadeOut: withSystem(
      {label: 'Fade out', defaultValue: 100, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('MSNumber')))
    ),
  }),
  temp: TRecord({
    tabs: TRecord({
      current: withSystem({defaultValue: '', private: true}, TText),
    }),
  }),
} as const;

export type Tab = {
  id: string;
  open: boolean;
};
const TAB_LINK_TYPE = qualify(Plugins.name('Tabs'), Component.name('TabsLink'));

const getTabNames = matchExpression({
  Element: (el) => {
    if (namesEqual(el.type, TAB_LINK_TYPE)) {
      const attr = Expressions.getIn(
        Expressions.getElementData(el),
        DATA_W_TAB_PATH
      );
      if (attr) return Expressions.stop([Expressions.getText(attr) || '']);
    }
    return Expressions.resume(emptyArray);
  },
  _: constant(Expressions.resume(emptyArray)),
});

export const parseTabs = (expr: Expression): Tab[] => {
  const tabsMenuExprPath = findTabsMenuExpressionPath(expr);
  const noneTab = {id: NONE_TAB_ID, open: false} as const;
  if (!tabsMenuExprPath) return [noneTab];
  const tabLinks = Expressions.getIn(expr, tabsMenuExprPath);
  if (!tabLinks) return [noneTab];
  const tabNames = Expressions.everything(concatTo)(getTabNames)(tabLinks);
  return [noneTab, ...tabNames.map((id) => ({id, open: false}))];
};

export const parseDefaultTabId = (expr: Expression) => {
  const defaultTabExpr = Expressions.getIn(expr, DEFAULT_TAB_PATH);
  if (defaultTabExpr == null) return NONE_TAB_ID;
  return Expressions.getText(defaultTabExpr) ?? NONE_TAB_ID;
};

export const parseCurrentTabId = (expr: Expression) => {
  const defaultTabId = parseDefaultTabId(expr);
  const currentTabExpr = Expressions.getIn(expr, TEMP_TABS_CURRENT_PATH);
  if (currentTabExpr == null) return defaultTabId;
  // An empty `temp.tabs.current` means "uninitialized" (the schema default),
  // so fall back to the persisted default — matching how the TabsWrapper
  // render atom resolves `current` (`tempCurrent || initialDefault`). This
  // keeps the canvas, Tabs settings, and delete-by-id logic aligned.
  const currentTabId = Expressions.getText(currentTabExpr);
  return currentTabId === '' ? defaultTabId : (currentTabId ?? defaultTabId);
};

const findTabsMenuExpressionPath = (expr: Expression) => {
  const tabsMenuPath = Expressions.findPaths(
    expr,
    matchExpression({
      Element: (el) =>
        namesEqual(
          el.type,
          qualify(Plugins.name('Tabs'), Component.name('TabsMenu'))
        ),
      _: constantFalse,
    })
  );
  return tabsMenuPath && tabsMenuPath[0];
};

const findTabsContentExpressionPath = (expr: Expression) => {
  const tabsContentPath = Expressions.findPaths(
    expr,
    matchExpression({
      Element: (el) =>
        namesEqual(
          el.type,
          qualify(Plugins.name('Tabs'), Component.name('TabsContent'))
        ),
      _: constantFalse,
    })
  );
  return tabsContentPath && tabsContentPath[0];
};

const parseTabLinks = (
  expr: Expression,
  tabsMenuExpressionPath: ExpressionPath
) => {
  return tabsMenuExpressionPath
    ? Expressions.getIn(expr, tabsMenuExpressionPath)
    : [];
};

/**
 * Compact UI component for Mini Toolbar.
 * Renders a simplified tab switcher with dropdown + arrow buttons.
 */
const CompactTabsControls = ({
  tabs: allTabs,
  currentTabId,
  onSelectTab,
}: {
  tabs: Array<{id: string; open: boolean}>;
  currentTabId: string;
  onSelectTab: (id: string) => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const tabs = allTabs.filter((t) => t.id !== NONE_TAB_ID);
  const currentIndex = tabs.findIndex((t) => t.id === currentTabId);

  const goToPrevTab = () => {
    if (tabs.length === 0 || currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    onSelectTab(tabs[prevIndex]?.id ?? NONE_TAB_ID);
  };

  const goToNextTab = () => {
    if (tabs.length === 0 || currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % tabs.length;
    onSelectTab(tabs[nextIndex]?.id ?? NONE_TAB_ID);
  };

  // Use tab ID as display name for now (typically "Home", "About", etc.)
  // TODO: Display actual tab names from text content (https://webflow.atlassian.net/browse/COL-6424)
  const currentTabName = tabs[currentIndex]?.id || 'Select tab';

  return (
    <HStack padding="$1">
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <MenuTrigger
          prefix={ElementTabsIcon}
          suffix={ChevronSmallDownIcon}
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
        >
          {currentTabName}
        </MenuTrigger>
        <MenuContent>
          {tabs.map((tab) => (
            <MenuItem
              key={tab.id}
              prefix={
                tab.id === currentTabId ? (
                  <CheckDefaultIcon colorVariant="primary" />
                ) : (
                  <View width="$4" />
                )
              }
              onClick={() => {
                onSelectTab(tab.id);
                setIsOpen(false);
              }}
            >
              {tab.id}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      <View paddingY="$1" alignSelf="stretch" display="flex">
        <Divider orientation="vertical" elevation={1} />
      </View>
      <IconButton
        Icon={ChevronSmallLeftIcon}
        onClick={goToPrevTab}
        variant="ghost"
        label="Previous tab"
      />
      <IconButton
        Icon={ChevronSmallRightIcon}
        onClick={goToNextTab}
        variant="ghost"
        label="Next tab"
      />
    </HStack>
  );
};

const Render = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => any;
}) => {
  const eeContext = useExpressionEditorContext();

  const strippedExpr = stripAll(expr);
  const tabsMenuExpressionPath = findTabsMenuExpressionPath(strippedExpr);
  const tabsContentExpressionPath = findTabsContentExpressionPath(strippedExpr);
  const [tabs, setTabs] = React.useState(parseTabs(strippedExpr));
  const [tabLinks, setTabLinks] = React.useState(
    // @ts-expect-error - TS2345 - Argument of type 'ExpressionPath | undefined' is not assignable to parameter of type 'ExpressionPath'.
    parseTabLinks(strippedExpr, tabsMenuExpressionPath)
  );
  const [currentTabId, setCurrentTabId] = React.useState(
    parseCurrentTabId(strippedExpr)
  );
  const [defaultTabId, setDefaultTabId] = React.useState(
    parseDefaultTabId(strippedExpr)
  );

  React.useEffect(() => {
    const nextTabs = parseTabs(strippedExpr);
    const validIds = new Set(nextTabs.map((t) => t.id));

    const nextCurrentTabId = parseCurrentTabId(strippedExpr);
    // If the viewed tab was deleted (e.g. by another user), reset to None.
    if (nextCurrentTabId && !validIds.has(nextCurrentTabId)) {
      const resetExpr = Expressions.sparseSetIn(
        strippedExpr,
        TEMP_TABS_CURRENT_PATH,
        EText(NONE_TAB_ID)
      );
      dispatch(updateExpressionAction(resetExpr));
      setCurrentTabId(NONE_TAB_ID);
    } else if (currentTabId !== nextCurrentTabId) {
      setCurrentTabId(nextCurrentTabId);
    }

    const nextDefaultTabId = parseDefaultTabId(strippedExpr);
    if (defaultTabId !== nextDefaultTabId) setDefaultTabId(nextDefaultTabId);

    if (tabsMenuExpressionPath) {
      const nextTabLinks = Expressions.getIn(
        stripAll(expr),
        tabsMenuExpressionPath
      );
      if (tabLinks !== nextTabLinks) {
        setTabLinks(nextTabLinks);
        setTabs(nextTabs);
      }
    }
  }, [expr]);

  const onChangeChildren = (childrenExpr: any) => {
    const newExpr = Expressions.setIn(
      stripAll(expr),
      CHILDREN_PATH,
      childrenExpr.payload.newExpression
    );
    dispatch(updateExpressionAction(newExpr));
  };

  const hasMenuAndContentChildExpressions = () => {
    return tabsMenuExpressionPath && tabsContentExpressionPath;
  };

  const getTabLinksListPath = (): ExpressionPath => [
    ...(tabsMenuExpressionPath ?? []),
    ...ELEMENT_CHILDREN_PATH,
  ];
  const getTabPaneListPath = (): ExpressionPath => [
    ...(tabsContentExpressionPath ?? []),
    ...ELEMENT_CHILDREN_PATH,
  ];

  const getTabLinksList = (expression: Expression) => {
    const tabsMenuExpression = Expressions.getIn(
      expression,
      tabsMenuExpressionPath || []
    );
    if (!tabsMenuExpression) return [];

    const tabsMenuElement = Expressions.getElement(tabsMenuExpression);
    if (!tabsMenuElement) return [];

    const tabLinksExpr = Expressions.getIn(
      Expressions.getElementData(tabsMenuElement),
      CHILDREN_PATH
    );

    if (!tabLinksExpr) return [];

    const tabLinksList = Expressions.getList(tabLinksExpr);
    if (!tabLinksList) return [];
    return tabLinksList;
  };

  const getTabPanesList = (expression: Expression) => {
    const tabsContentExpression = Expressions.getIn(
      expression,
      // @ts-expect-error - TS2345 - Argument of type 'ExpressionPath | undefined' is not assignable to parameter of type 'ExpressionPath'.
      tabsContentExpressionPath
    );
    if (!tabsContentExpression) return [];

    const tabsContentElement = Expressions.getElement(tabsContentExpression);
    if (!tabsContentElement) return [];

    const tabPanesExpr = Expressions.getIn(
      Expressions.getElementData(tabsContentElement),
      CHILDREN_PATH
    );

    if (!tabPanesExpr) return [];

    const tabPanesList = Expressions.getList(tabPanesExpr);
    if (!tabPanesList) return [];
    return tabPanesList;
  };

  const deleteTab = (deleteIdx: PropertyListItemID) => {
    if (!hasMenuAndContentChildExpressions()) return;
    const tabLinksList = getTabLinksList(strippedExpr);
    const tabPanesList = getTabPanesList(strippedExpr);

    const deletedTab = tabs[parseInt(deleteIdx, 10)];
    if (!deleteTab) return;
    // @ts-expect-error - TS18048 - 'deletedTab' is possibly 'undefined'.
    const deleteId = deletedTab.id;

    const newTabLinksList = tabLinksList.filter((tabLink) => {
      const tabLinkElement = Expressions.getElement(tabLink);
      if (!tabLinkElement) return false;
      const id = Expressions.getIn(
        Expressions.getElementData(tabLinkElement),
        DATA_W_TAB_PATH
      );
      const currId = id && Expressions.getText(id);
      return currId !== deleteId;
    });

    const newTabPanesList = tabPanesList.filter((tabPane) => {
      const tabLinkElement = Expressions.getElement(tabPane);
      if (!tabLinkElement) return false;
      const id = Expressions.getIn(
        Expressions.getElementData(tabLinkElement),
        DATA_W_TAB_PATH
      );
      const currId = id && Expressions.getText(id);
      return currId !== deleteId;
    });

    let newExpr = Expressions.setIn(
      strippedExpr,
      getTabLinksListPath(),
      EList(newTabLinksList)
    );

    newExpr = Expressions.setIn(
      newExpr,
      getTabPaneListPath(),
      EList(newTabPanesList)
    );

    // Reset the current viewed tab to None if the deleted tab was selected.
    if (deleteId === currentTabId)
      newExpr = Expressions.sparseSetIn(
        newExpr,
        TEMP_TABS_CURRENT_PATH,
        EText(NONE_TAB_ID)
      );
    if (deleteId === defaultTabId)
      newExpr = Expressions.setIn(
        newExpr,
        DEFAULT_TAB_PATH,
        EText(NONE_TAB_ID)
      );

    dispatch(updateExpressionAction(newExpr));
  };

  const editTab = (oldId: string, newId: string) => {
    if (!hasMenuAndContentChildExpressions()) return;
    const tabLinksList = getTabLinksList(strippedExpr);
    const tabPanesList = getTabPanesList(strippedExpr);

    const newTabLinksList = tabLinksList.map((tabLink) => {
      const tabLinkElement = Expressions.getElement(tabLink);
      if (!tabLinkElement) return tabLink;
      const id = Expressions.getIn(
        Expressions.getElementData(tabLinkElement),
        DATA_W_TAB_PATH
      );
      const currId = id && Expressions.getText(id);
      if (currId !== oldId) return tabLink;
      return Expressions.setIn(
        tabLink,
        [{in: 'Element'}, ...DATA_W_TAB_PATH],
        EText(newId)
      );
    });

    const newTabPanesList = tabPanesList.map((tabLink) => {
      const tabLinkElement = Expressions.getElement(tabLink);
      if (!tabLinkElement) return tabLink;
      const id = Expressions.getIn(
        Expressions.getElementData(tabLinkElement),
        DATA_W_TAB_PATH
      );
      const currId = id && Expressions.getText(id);
      if (currId !== oldId) return tabLink;
      return Expressions.setIn(
        tabLink,
        [{in: 'Element'}, ...DATA_W_TAB_PATH],
        EText(newId)
      );
    });

    let newExpr = Expressions.setIn(
      strippedExpr,
      getTabLinksListPath(),
      EList(newTabLinksList)
    );
    newExpr = Expressions.setIn(
      newExpr,
      getTabPaneListPath(),
      EList(newTabPanesList)
    );
    // if current tab edited, it stays selected
    if (oldId === currentTabId)
      newExpr = Expressions.sparseSetIn(
        newExpr,
        TEMP_TABS_CURRENT_PATH,
        EText(newId)
      );
    if (oldId === defaultTabId)
      newExpr = Expressions.setIn(newExpr, DEFAULT_TAB_PATH, EText(newId));

    dispatch(updateExpressionAction(newExpr));
  };

  const selectTab = (id: string) => {
    const newExpr = Expressions.sparseSetIn(
      strippedExpr,
      TEMP_TABS_CURRENT_PATH,
      EText(id)
    );
    dispatch(updateExpressionAction(newExpr));
  };

  const arrayMove = (
    arr: Array<Expression>,
    fromIndex: number,
    toIndex: number
  ) => {
    const newArray = [...arr];
    const item = newArray.splice(fromIndex, 1)[0];
    // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
    newArray.splice(toIndex, 0, item);
    return newArray;
  };

  // @ts-expect-error - TS7031 - Binding element 'itemId' implicitly has an 'any' type. | TS7031 - Binding element 'dropTargetId' implicitly has an 'any' type.
  const onSortEnd = ({itemId, dropTargetId}) => {
    if (!hasMenuAndContentChildExpressions()) return;
    const startIndex = parseInt(itemId, 10);
    const endIndex = parseInt(dropTargetId, 10);
    const tabLinksList = getTabLinksList(strippedExpr);
    const tabPanesList = getTabPanesList(strippedExpr);
    const newTabLinksList = arrayMove(
      tabLinksList,
      startIndex - 1,
      endIndex - 1
    );
    const newTabPanesList = arrayMove(
      tabPanesList,
      startIndex - 1,
      endIndex - 1
    );

    let newExpr = Expressions.setIn(
      strippedExpr,
      getTabLinksListPath(),
      EList(newTabLinksList)
    );
    newExpr = Expressions.setIn(
      newExpr,
      getTabPaneListPath(),
      EList(newTabPanesList)
    );

    dispatch(updateExpressionAction(newExpr));
  };

  const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

  // a helper function to pass nested expression to ExpressionEditor
  const expressionEditorAtPath = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'path' implicitly has an 'any' type. | TS7006 - Parameter 'newDispatch' implicitly has an 'any' type.
    (path, newDispatch) => {
      const subExpr = ContextualExpressions.getIn(expr, path);
      if (!subExpr)
        throw Error(
          `failed to get expression at path: ${JSON.stringify(path)}`
        );

      return (
        <ExpressionEditor
          expression={subExpr}
          dispatch={newDispatch || dispatchAt(path)}
        />
      );
    },
    [expr, dispatchAt]
  );

  if (eeContext === 'compact') {
    return (
      <CompactTabsControls
        tabs={tabs}
        currentTabId={currentTabId}
        onSelectTab={selectTab}
      />
    );
  }

  return (
    <TabsContext.Provider value={{tabs}}>
      <StyledLabelContainer>
        <span>Tabs</span>
        {expressionEditorAtPath(CHILDREN_PATH, onChangeChildren)}
      </StyledLabelContainer>
      <TabsList
        items={tabs}
        onItemDeleted={deleteTab}
        onItemDropped={onSortEnd}
        onSelectClick={selectTab}
        onEditClick={editTab}
        currentTabId={currentTabId}
        defaultTabId={defaultTabId}
      />
      <Separator />
      {/* @ts-expect-error - TS2554 - Expected 2 arguments, but got 1. */}
      {expressionEditorAtPath(DEFAULT_TAB_PATH)}
      <Separator />
      {/* @ts-expect-error - TS2554 - Expected 2 arguments, but got 1. */}
      {expressionEditorAtPath(EASING_PATH)}
      <Separator />
      {/* @ts-expect-error - TS2554 - Expected 2 arguments, but got 1. */}
      {expressionEditorAtPath(FADE_IN_PATH)}
      <Separator />
      {/* @ts-expect-error - TS2554 - Expected 2 arguments, but got 1. */}
      {expressionEditorAtPath(FADE_OUT_PATH)}
    </TabsContext.Provider>
  );
};

export const TabsWrapperType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: Render,
});
